import pako from "pako";

/**
 * Read a NIfTI file's raw voxel data directly — no cornerstone volume loader.
 *
 * Why this exists: routing a segmentation NIfTI through
 * createNiftiImageIds + volumeLoader.createAndCacheVolume produces a fresh
 * `blob:` object URL on every call, and the resulting imageIds fail to resolve
 * ("Image not found for imageId: nifti:blob:..."), so the volume loads with an
 * EMPTY scalar array. We only need the label values, not a renderable volume,
 * so we parse the file ourselves.
 *
 * Supports .nii and .nii.gz, NIfTI-1 and NIfTI-2, with the common integer /
 * float datatypes used by label maps.
 */

export interface NiftiVoxelData {
  /** Raw voxel values, length = dims[0]*dims[1]*dims[2] */
  data: Float64Array | Int32Array | Uint8Array | Int16Array | Uint16Array;
  /** [x, y, z] */
  dimensions: [number, number, number];
  /** [x, y, z] voxel size in mm */
  spacing: [number, number, number];
  /** Volume of a single voxel in mm³ */
  voxelVolumeMm3: number;
}

const NIFTI1_MAGIC = "n+1";
const NIFTI2_MAGIC = "n+2";

// NIfTI datatype codes
const DT = {
  UINT8: 2,
  INT16: 4,
  INT32: 8,
  FLOAT32: 16,
  FLOAT64: 64,
  INT8: 256,
  UINT16: 512,
  UINT32: 768,
} as const;

function isGzipped(bytes: Uint8Array): boolean {
  return bytes[0] === 0x1f && bytes[1] === 0x8b;
}

function readMagic(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
  );
}

/**
 * Parse a NIfTI ArrayBuffer into voxel data + geometry.
 */
export function parseNifti(buffer: ArrayBuffer): NiftiVoxelData {
  let bytes = new Uint8Array(buffer);

  if (isGzipped(bytes)) {
    bytes = pako.inflate(bytes);
  }

  const buf = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
  const view = new DataView(buf);

  // sizeof_hdr tells us endianness and version: 348 => NIfTI-1, 540 => NIfTI-2
  let littleEndian = true;
  let sizeofHdr = view.getInt32(0, true);
  if (sizeofHdr !== 348 && sizeofHdr !== 540) {
    littleEndian = false;
    sizeofHdr = view.getInt32(0, false);
  }
  if (sizeofHdr !== 348 && sizeofHdr !== 540) {
    throw new Error(`Not a NIfTI file (sizeof_hdr=${sizeofHdr})`);
  }

  const isV2 = sizeofHdr === 540;

  let dims: number[];
  let pixdims: number[];
  let datatype: number;
  let voxOffset: number;
  let sclSlope: number;
  let sclInter: number;

  if (isV2) {
    // NIfTI-2 layout
    const magic = readMagic(view, 4);
    if (magic !== NIFTI2_MAGIC && magic !== NIFTI1_MAGIC) {
      // magic check is lenient; some writers vary
    }
    datatype = view.getInt16(12, littleEndian);
    dims = [];
    for (let i = 0; i < 8; i++) {
      dims.push(Number(view.getBigInt64(16 + i * 8, littleEndian)));
    }
    pixdims = [];
    for (let i = 0; i < 8; i++) {
      pixdims.push(view.getFloat64(104 + i * 8, littleEndian));
    }
    voxOffset = Number(view.getBigInt64(168, littleEndian));
    sclSlope = view.getFloat64(176, littleEndian);
    sclInter = view.getFloat64(184, littleEndian);
  } else {
    // NIfTI-1 layout
    datatype = view.getInt16(70, littleEndian);
    dims = [];
    for (let i = 0; i < 8; i++) {
      dims.push(view.getInt16(40 + i * 2, littleEndian));
    }
    pixdims = [];
    for (let i = 0; i < 8; i++) {
      pixdims.push(view.getFloat32(76 + i * 4, littleEndian));
    }
    voxOffset = view.getFloat32(108, littleEndian);
    sclSlope = view.getFloat32(112, littleEndian);
    sclInter = view.getFloat32(116, littleEndian);
  }

  const nx = dims[1] || 1;
  const ny = dims[2] || 1;
  const nz = dims[3] || 1;
  const numVoxels = nx * ny * nz;

  const sx = Math.abs(pixdims[1]) || 1;
  const sy = Math.abs(pixdims[2]) || 1;
  const sz = Math.abs(pixdims[3]) || 1;

  const dataOffset = Math.max(voxOffset, isV2 ? 540 : 348);

  let data: NiftiVoxelData["data"];
  switch (datatype) {
    case DT.UINT8:
      data = new Uint8Array(buf, dataOffset, numVoxels);
      break;
    case DT.INT8: {
      const src = new Int8Array(buf, dataOffset, numVoxels);
      data = Int16Array.from(src);
      break;
    }
    case DT.INT16:
      data = littleEndianSafe(buf, dataOffset, numVoxels, 2, littleEndian)
        ? new Int16Array(buf, dataOffset, numVoxels)
        : readTyped(view, dataOffset, numVoxels, 2, littleEndian, "int16");
      break;
    case DT.UINT16:
      data = littleEndianSafe(buf, dataOffset, numVoxels, 2, littleEndian)
        ? new Uint16Array(buf, dataOffset, numVoxels)
        : readTyped(view, dataOffset, numVoxels, 2, littleEndian, "uint16");
      break;
    case DT.INT32:
      data = littleEndianSafe(buf, dataOffset, numVoxels, 4, littleEndian)
        ? new Int32Array(buf, dataOffset, numVoxels)
        : readTyped(view, dataOffset, numVoxels, 4, littleEndian, "int32");
      break;
    case DT.UINT32: {
      const out = new Int32Array(numVoxels);
      for (let i = 0; i < numVoxels; i++) {
        out[i] = view.getUint32(dataOffset + i * 4, littleEndian);
      }
      data = out;
      break;
    }
    case DT.FLOAT32: {
      const out = new Int32Array(numVoxels);
      for (let i = 0; i < numVoxels; i++) {
        out[i] = Math.round(view.getFloat32(dataOffset + i * 4, littleEndian));
      }
      data = out;
      break;
    }
    case DT.FLOAT64: {
      const out = new Int32Array(numVoxels);
      for (let i = 0; i < numVoxels; i++) {
        out[i] = Math.round(view.getFloat64(dataOffset + i * 8, littleEndian));
      }
      data = out;
      break;
    }
    default:
      throw new Error(`Unsupported NIfTI datatype: ${datatype}`);
  }

  // Apply scl_slope/scl_inter if it's a real scaling (labels normally have none)
  if (sclSlope && sclSlope !== 1 && Number.isFinite(sclSlope)) {
    const scaled = new Int32Array(numVoxels);
    for (let i = 0; i < numVoxels; i++) {
      scaled[i] = Math.round(data[i] * sclSlope + (sclInter || 0));
    }
    data = scaled;
  }

  return {
    data,
    dimensions: [nx, ny, nz],
    spacing: [sx, sy, sz],
    voxelVolumeMm3: sx * sy * sz,
  };
}

/** TypedArray views require the byteOffset to be aligned to the element size. */
function littleEndianSafe(
  buf: ArrayBuffer,
  offset: number,
  _n: number,
  bytesPerEl: number,
  littleEndian: boolean,
): boolean {
  return littleEndian && offset % bytesPerEl === 0;
}

function readTyped(
  view: DataView,
  offset: number,
  n: number,
  bytesPerEl: number,
  littleEndian: boolean,
  kind: "int16" | "uint16" | "int32",
): any {
  const out =
    kind === "int16"
      ? new Int16Array(n)
      : kind === "uint16"
      ? new Uint16Array(n)
      : new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const o = offset + i * bytesPerEl;
    out[i] =
      kind === "int16"
        ? view.getInt16(o, littleEndian)
        : kind === "uint16"
        ? view.getUint16(o, littleEndian)
        : view.getInt32(o, littleEndian);
  }
  return out;
}

/** Fetch + parse a NIfTI file's voxel data directly from a URL. */
export default async function readNiftiVoxels(
  url: string,
): Promise<NiftiVoxelData> {
  const resp = await fetch(url, { credentials: "include" });
  if (!resp.ok) {
    throw new Error(`Fetch failed for ${url}: ${resp.status}`);
  }
  const buffer = await resp.arrayBuffer();
  return parseNifti(buffer);
}

/**
 * Count voxels per label value. Returns Map<label, voxelCount>, skipping 0
 * (background). Feeds the "compute volumes straight from the NIfTI" path:
 * volume_mL = count * voxelVolumeMm3 / 1000.
 */
export function countLabelVoxels(voxels: NiftiVoxelData): Map<number, number> {
  const counts = new Map<number, number>();
  const { data } = voxels;
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    if (v > 0) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return counts;
}
